import { AUTH_FRONTEND_URL, PROJECT_SLUG } from '../config/constants.js';

/**
 * Silent re-authentication via the IAM's deep-link (OIDC `prompt=none` style).
 *
 * On reload / token expiry the in-memory access token is gone. Instead of forcing a manual
 * re-login, we do a top-level redirect to the IAM with `?to=<slug>&returnTo=<path>`. If the
 * user still has an active IAM session (httpOnly cookie, first-party), the IAM auto-issues a
 * handoff for this project and bounces back to the exact path with `#code` — no picker, no
 * credentials. The access token never leaves memory; only a timestamp guard lives in storage.
 *
 * A cooldown prevents redirect loops: if we just bounced to the IAM and came back still
 * unauthenticated (e.g. no IAM session, or no role on this project), we stop retrying and let
 * the caller show a manual login fallback.
 */
const ATTEMPT_KEY = '_trl_silent_auth_ts';
const COOLDOWN_MS = 15_000;

/**
 * Redirect to the IAM silent-authorize endpoint, unless we attempted it very recently (loop
 * guard). Returns true if it actually navigated, false if suppressed by the cooldown.
 * Pass `force` for an explicit user action (manual "Ingresar" button).
 */
export function goToIamAuth(returnToPath: string, opts: { force?: boolean } = {}): boolean {
  if (!opts.force) {
    const last = Number(sessionStorage.getItem(ATTEMPT_KEY) ?? '0');
    if (Date.now() - last < COOLDOWN_MS) return false;
  }
  sessionStorage.setItem(ATTEMPT_KEY, String(Date.now()));
  // Dedicated silent-authorize launcher (not the interactive /projects picker, which must never
  // be hijacked by a sticky ?to). On no session / no access it falls back to the picker.
  const url =
    `${AUTH_FRONTEND_URL}/launch` +
    `?to=${encodeURIComponent(PROJECT_SLUG)}` +
    `&returnTo=${encodeURIComponent(returnToPath)}`;
  window.location.href = url;
  return true;
}

/** Clear the loop guard after a successful authentication so future renewals aren't blocked. */
export function clearAuthAttempt(): void {
  sessionStorage.removeItem(ATTEMPT_KEY);
}
